import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import {
  getDataUploadConfig,
  updateDataUploadConfig,
  getDataUploadPreview,
  listDataUploadRuns,
  downloadDataUploadFile,
  uploadDataUploadSource,
  setDataUploadSourceEnabled,
  deleteDataUploadSource,
} from '../../lib/api/admin';
import type {
  DataUploadConfig,
  DataUploadConfigUpdate,
  DataUploadPreview,
  DataUploadRun,
} from '../../lib/api/admin';
import { fmtDateTime, fmtNum } from '../../lib/format';

const INPUT_CLASS =
  'w-full rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white focus:border-white/40 focus:outline-none [color-scheme:dark]';

const STATUS_STYLES: Record<string, string> = {
  SUCCESS: 'bg-emerald-400/20 text-emerald-200',
  FAILED: 'bg-rose-500/20 text-rose-200',
  PENDING: 'bg-amber-400/20 text-amber-100',
};

const ACTION_LABELS: Record<string, string> = {
  download: 'Encrypted download',
  'download-csv': 'CSV download',
};

interface Draft {
  eventCode: string;
  eventEndDate: string; // YYYY-MM-DD or ''
  includeRegistrants: boolean;
}

function toDraft(c: DataUploadConfig): Draft {
  return {
    eventCode: c.eventCode,
    eventEndDate: c.eventEndDate ?? '',
    includeRegistrants: c.includeRegistrants,
  };
}

function buildUpdate(d: Draft): DataUploadConfigUpdate {
  return {
    eventCode: d.eventCode.trim(),
    eventEndDate: d.eventEndDate ? d.eventEndDate : null,
    includeRegistrants: d.includeRegistrants,
  };
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wide text-white/55">{label}</span>
      {children}
      {hint ? <span className="text-[11px] text-white/40">{hint}</span> : null}
    </label>
  );
}

export function DataUploadTab() {
  const [config, setConfig] = useState<DataUploadConfig | null>(null);
  const [baseline, setBaseline] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [preview, setPreview] = useState<DataUploadPreview | null>(null);

  const [runs, setRuns] = useState<DataUploadRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);

  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const dirty = useMemo(
    () => !!draft && !!baseline && JSON.stringify(draft) !== JSON.stringify(baseline),
    [draft, baseline],
  );

  const loadConfig = useCallback((signal?: AbortSignal) => {
    setLoading(true);
    getDataUploadConfig({ signal })
      .then((c) => {
        setConfig(c);
        const d = toDraft(c);
        setDraft(d);
        setBaseline(d);
        setLoadError(null);
      })
      .catch((err) => {
        if (signal?.aborted) return;
        setLoadError(err instanceof ApiError ? `Couldn't load config (${err.status}).` : "Couldn't load config.");
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  const loadPreview = useCallback((signal?: AbortSignal) => {
    getDataUploadPreview({ signal })
      .then((p) => setPreview(p))
      .catch(() => {
        /* preview is best-effort; the config/actions still work */
      });
  }, []);

  const loadRuns = useCallback((signal?: AbortSignal) => {
    setRunsLoading(true);
    listDataUploadRuns({ limit: 25, offset: 0 }, { signal })
      .then((r) => setRuns(r.runs))
      .catch(() => {
        /* leave prior list on transient error */
      })
      .finally(() => {
        if (!signal?.aborted) setRunsLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadConfig(controller.signal);
    loadPreview(controller.signal);
    loadRuns(controller.signal);
    return () => controller.abort();
  }, [loadConfig, loadPreview, loadRuns]);

  const patch = (p: Partial<Draft>): void => setDraft((prev) => (prev ? { ...prev, ...p } : prev));

  const onSave = (): void => {
    if (!draft) return;
    setSaving(true);
    setSaveMsg(null);
    setSaveError(null);
    updateDataUploadConfig(buildUpdate(draft))
      .then((updated) => {
        setConfig(updated);
        const d = toDraft(updated);
        setDraft(d);
        setBaseline(d);
        setSaveMsg('Saved.');
        loadPreview();
      })
      .catch((err) => {
        setSaveError(err instanceof ApiError ? err.message : 'Save failed.');
      })
      .finally(() => setSaving(false));
  };

  const onDownload = (action: 'download' | 'download-csv'): void => {
    setActionBusy(action);
    setActionMsg(null);
    setActionError(null);
    downloadDataUploadFile(action)
      .then(({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setActionMsg(`Downloaded ${filename}.`);
        loadRuns();
      })
      .catch((err) => {
        setActionError(err instanceof ApiError ? err.message : 'Download failed.');
      })
      .finally(() => setActionBusy(null));
  };

  // ── Partner sources ───────────────────────────────────
  const [srcKey, setSrcKey] = useState('');
  const [srcLabel, setSrcLabel] = useState('');
  const [srcFile, setSrcFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [srcMsg, setSrcMsg] = useState<string | null>(null);
  const [srcError, setSrcError] = useState<string | null>(null);
  const [sourceBusy, setSourceBusy] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const onUpload = (): void => {
    if (!srcFile || !srcKey.trim() || !srcLabel.trim()) {
      setSrcError('Source key, label, and a CSV file are all required.');
      return;
    }
    setUploading(true);
    setSrcMsg(null);
    setSrcError(null);
    uploadDataUploadSource({ sourceKey: srcKey.trim(), label: srcLabel.trim(), file: srcFile })
      .then((s) => {
        setSrcMsg(`Uploaded "${s.label}": ${s.includedCount} opted-in of ${s.rowCount} rows.`);
        setSrcKey('');
        setSrcLabel('');
        setSrcFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        loadPreview();
      })
      .catch((err) => setSrcError(err instanceof ApiError ? err.message : 'Upload failed.'))
      .finally(() => setUploading(false));
  };

  const onToggleSource = (sourceKey: string, enabled: boolean): void => {
    setSourceBusy(sourceKey);
    setSrcError(null);
    setDataUploadSourceEnabled(sourceKey, enabled)
      .then(() => loadPreview())
      .catch((err) => setSrcError(err instanceof ApiError ? err.message : 'Update failed.'))
      .finally(() => setSourceBusy(null));
  };

  const onDeleteSource = (sourceKey: string): void => {
    if (!window.confirm(`Delete source "${sourceKey}" and all its rows?`)) return;
    setSourceBusy(sourceKey);
    setSrcError(null);
    deleteDataUploadSource(sourceKey)
      .then(() => loadPreview())
      .catch((err) => setSrcError(err instanceof ApiError ? err.message : 'Delete failed.'))
      .finally(() => setSourceBusy(null));
  };

  if (loading && !config) {
    return <p className="text-white/60">Loading data upload settings…</p>;
  }
  if (loadError && !config) {
    return <p className="text-sm text-[#ff8da6]">{loadError}</p>;
  }
  if (!draft || !config) return null;

  const busy = actionBusy !== null;

  return (
    <div className="flex flex-col gap-10">
      {/* ── Settings ─────────────────────────────────────────── */}
      <section className="flex flex-col gap-4">
        <div>
          <h2 className="font-heading text-lg text-white">MoZeus LiveRamp file</h2>
          <p className="mt-1 text-sm text-white/55">
            Merges opted-in registrants with uploaded partner-experience data into a PGP-encrypted
            LiveRamp CSV. Download the encrypted file and upload it to the MoZeus ShareFile folder.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Event code" hint="MoZeus-assigned campaign key (EventID column).">
            <input
              className={INPUT_CLASS}
              value={draft.eventCode}
              onChange={(e) => patch({ eventCode: e.target.value })}
            />
          </Field>
          <Field label="Event end date" hint="Written as MM/DD/YYYY in the file.">
            <input
              type="date"
              className={INPUT_CLASS}
              value={draft.eventEndDate}
              onChange={(e) => patch({ eventEndDate: e.target.value })}
            />
          </Field>
          <div className="flex items-center pt-6 sm:col-span-2">
            <label className="flex items-center gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                className="h-4 w-4 accent-emerald-400"
                checked={draft.includeRegistrants}
                onChange={(e) => patch({ includeRegistrants: e.target.checked })}
              />
              Include GHS5 registrants (opted-in)
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || saving}
            className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save settings'}
          </button>
          {dirty ? <span className="text-xs text-[#ffae5b]">Unsaved changes</span> : null}
          {saveMsg ? <span className="text-sm text-[#5bffb4]">{saveMsg}</span> : null}
          {saveError ? <span className="text-sm text-[#ff8da6]">{saveError}</span> : null}
        </div>
      </section>

      {/* ── Partner data sources ─────────────────────────────── */}
      <section className="flex flex-col gap-4">
        <div>
          <h2 className="font-heading text-lg text-white">Partner data sources</h2>
          <p className="mt-1 text-sm text-white/55">
            Upload FlowCode/DreamCon, 100 Thieves, or any partner CSV. Required headers:{' '}
            <span className="text-white/70">Email</span> and <span className="text-white/70">OptIn</span> (plus optional
            FirstName, LastName, Address…Zip). The raw FlowCode export works as-is. Only opted-in rows are merged;
            re-uploading a source key replaces its rows.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Source key" hint="Lowercase slug, e.g. dreamcon.">
            <input
              className={INPUT_CLASS}
              placeholder="dreamcon"
              value={srcKey}
              onChange={(e) => setSrcKey(e.target.value)}
            />
          </Field>
          <Field label="Label" hint="Display name.">
            <input
              className={INPUT_CLASS}
              placeholder="DreamCon (FlowCode)"
              value={srcLabel}
              onChange={(e) => setSrcLabel(e.target.value)}
            />
          </Field>
          <Field label="CSV file">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className={INPUT_CLASS}
              onChange={(e) => setSrcFile(e.target.files?.[0] ?? null)}
            />
          </Field>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onUpload}
            disabled={uploading}
            className="rounded-full bg-white/90 px-5 py-2 text-sm font-medium text-[#2a2237] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : 'Upload source'}
          </button>
          {srcMsg ? <span className="text-sm text-[#5bffb4]">{srcMsg}</span> : null}
          {srcError ? <span className="text-sm text-[#ff8da6]">{srcError}</span> : null}
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/15 bg-black/20">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/55">
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Rows</th>
                <th className="px-4 py-2 font-medium">Opted-in</th>
                <th className="px-4 py-2 font-medium">Uploaded</th>
                <th className="px-4 py-2 font-medium">Included</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {!preview || preview.sources.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-white/50">
                    No sources uploaded yet.
                  </td>
                </tr>
              ) : (
                preview.sources.map((s) => (
                  <tr key={s.sourceKey} className="border-b border-white/5">
                    <td className="px-4 py-2 text-white/80">
                      <div className="text-white">{s.label}</div>
                      <div className="text-xs text-white/45">
                        {s.sourceKey}
                        {s.fileName ? ` · ${s.fileName}` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-white/80">{fmtNum(s.rowCount)}</td>
                    <td className="px-4 py-2 text-white/80">{fmtNum(s.includedCount)}</td>
                    <td className="px-4 py-2 text-white/60">{fmtDateTime(s.uploadedAt)}</td>
                    <td className="px-4 py-2">
                      <label className="flex items-center gap-2 text-xs text-white/70">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-emerald-400"
                          checked={s.enabled}
                          disabled={sourceBusy === s.sourceKey}
                          onChange={(e) => onToggleSource(s.sourceKey, e.target.checked)}
                        />
                        {s.enabled ? 'Yes' : 'No'}
                      </label>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => onDeleteSource(s.sourceKey)}
                        disabled={sourceBusy === s.sourceKey}
                        className="text-xs text-[#ff8da6] hover:underline disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Preview + download ───────────────────────────────── */}
      <section className="flex flex-col gap-4">
        <h2 className="font-heading text-lg text-white">Generate &amp; download</h2>

        {preview ? (
          <div className="flex flex-wrap gap-6 rounded-xl border border-white/15 bg-black/20 px-5 py-4 text-sm">
            <div>
              <div className="text-white/50">Registrants (opted-in)</div>
              <div className="text-lg text-white">
                {preview.includeRegistrants ? fmtNum(preview.registrantCount) : 'excluded'}
              </div>
            </div>
            <div>
              <div className="text-white/50">Partner sources</div>
              <div className="text-lg text-white">{preview.sources.filter((s) => s.enabled).length}</div>
            </div>
            <div>
              <div className="text-white/50">Est. rows (pre-dedupe)</div>
              <div className="text-lg text-white">{fmtNum(preview.estimatedRowsBeforeDedupe)}</div>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => onDownload('download')}
            disabled={busy}
            className="rounded-full bg-brand-redAccent px-5 py-2 text-sm font-medium text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionBusy === 'download' ? 'Preparing…' : 'Download encrypted (.csv.pgp)'}
          </button>
          <button
            type="button"
            onClick={() => onDownload('download-csv')}
            disabled={busy}
            className="rounded-full border border-white/25 px-5 py-2 text-sm font-medium text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionBusy === 'download-csv' ? 'Preparing…' : 'Download CSV (verify)'}
          </button>
        </div>
        <p className="text-xs text-white/40">
          The encrypted <span className="text-white/60">.csv.pgp</span> is the MoZeus deliverable. The plain CSV is
          for your own verification only — do not send it.
        </p>
        {actionMsg ? <p className="text-sm text-[#5bffb4]">{actionMsg}</p> : null}
        {actionError ? <p className="text-sm text-[#ff8da6]">{actionError}</p> : null}
      </section>

      {/* ── Generation history ───────────────────────────────── */}
      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg text-white">Generation history</h2>
          <button
            type="button"
            onClick={() => loadRuns()}
            className="text-xs uppercase tracking-wide text-white/55 hover:text-white/85"
          >
            Refresh
          </button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-white/15 bg-black/20">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/55">
                <th className="px-4 py-2 font-medium">When</th>
                <th className="px-4 py-2 font-medium">By</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Rows</th>
                <th className="px-4 py-2 font-medium">File</th>
              </tr>
            </thead>
            <tbody>
              {runsLoading && runs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-white/50">
                    Loading history…
                  </td>
                </tr>
              ) : runs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-white/50">
                    No files generated yet.
                  </td>
                </tr>
              ) : (
                runs.map((run) => (
                  <tr key={run.runId} className="border-b border-white/5 align-top">
                    <td className="px-4 py-2 text-white/80">{fmtDateTime(run.created)}</td>
                    <td className="px-4 py-2 text-white/70">{run.createdByEmail ?? '—'}</td>
                    <td className="px-4 py-2 text-white/80">{ACTION_LABELS[run.action] ?? run.action}</td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block rounded-full px-2 py-0.5 text-xs ${
                          STATUS_STYLES[run.status] ?? 'bg-white/10 text-white/70'
                        }`}
                      >
                        {run.status}
                      </span>
                      {run.error ? <div className="mt-1 text-xs text-[#ff8da6]">{run.error}</div> : null}
                    </td>
                    <td className="px-4 py-2 text-white/80">{fmtNum(run.totalRows)}</td>
                    <td className="px-4 py-2 text-white/60">{run.fileName ?? '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
